// Optional extra materials a recruiter can attach on the schedule wizard's
// Materials step. Each maps onto a neurax.end_client_materials.material_type
// that generate-interview-prompt already renders as its own prompt section.

export type AdditionalMaterialType =
  | 'company_info'
  | 'study_material'
  | 'interview_tips'
  | 'custom';

export type AdditionalMaterialSource =
  | { kind: 'file'; file: File }
  | { kind: 'text'; text: string };

export interface AdditionalMaterialInput {
  type: AdditionalMaterialType;
  title: string;
  source: AdditionalMaterialSource;
}

export interface AdditionalMaterialDraft {
  id: string;
  type: AdditionalMaterialType;
  title: string;
  mode: 'file' | 'text';
  file: File | null;
  text: string;
}

export const ADDITIONAL_MATERIAL_LIMITS = {
  maxItems: 5,
  maxFileBytes: 10 * 1024 * 1024,
  maxTextChars: 20_000,
} as const;

export const ADDITIONAL_MATERIAL_TYPES: {
  value: AdditionalMaterialType;
  label: string;
  placeholder: string;
}[] = [
  {
    value: 'company_info',
    label: 'Company info',
    placeholder: 'Company overview, products, values, recent news, team structure…',
  },
  {
    value: 'study_material',
    label: 'Study material',
    placeholder: 'Topics to revise, prep guides, sample questions, technical notes…',
  },
  {
    value: 'interview_tips',
    label: 'Interview tips',
    placeholder: 'Interviewer style, what the panel looks for, things to emphasize or avoid…',
  },
  {
    value: 'custom',
    label: 'Other',
    placeholder: 'Anything else the AI should know for this session…',
  },
];

export function additionalMaterialTypeLabel(type: AdditionalMaterialType): string {
  return ADDITIONAL_MATERIAL_TYPES.find((t) => t.value === type)?.label ?? 'Material';
}

export function createAdditionalMaterialDraft(): AdditionalMaterialDraft {
  return {
    id: crypto.randomUUID(),
    type: 'company_info',
    title: '',
    mode: 'file',
    file: null,
    text: '',
  };
}

export function validateAdditionalMaterials(drafts: AdditionalMaterialDraft[]): string | null {
  const { maxItems, maxFileBytes, maxTextChars } = ADDITIONAL_MATERIAL_LIMITS;
  if (drafts.length > maxItems) return `You can add up to ${maxItems} additional materials.`;

  for (const [i, d] of drafts.entries()) {
    const label = `additional material ${i + 1}`;
    if (d.mode === 'file') {
      if (!d.file) return `Upload a file for ${label}, or remove it.`;
      if (d.file.size > maxFileBytes) return `The file for ${label} is over 10 MB.`;
    } else {
      if (!d.text.trim()) return `Paste some text for ${label}, or remove it.`;
      if (d.text.length > maxTextChars) {
        return `The text for ${label} is over ${maxTextChars.toLocaleString('en-US')} characters.`;
      }
    }
  }
  return null;
}

export function toAdditionalMaterialInputs(
  drafts: AdditionalMaterialDraft[]
): AdditionalMaterialInput[] {
  const inputs: AdditionalMaterialInput[] = [];
  for (const d of drafts) {
    const customTitle = d.title.trim();
    if (d.mode === 'file') {
      if (!d.file) continue;
      inputs.push({
        type: d.type,
        title: customTitle || d.file.name,
        source: { kind: 'file', file: d.file },
      });
    } else {
      const text = d.text.trim();
      if (!text) continue;
      inputs.push({
        type: d.type,
        title: customTitle || additionalMaterialTypeLabel(d.type),
        source: { kind: 'text', text },
      });
    }
  }
  return inputs;
}
